import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from '@/lib/api';

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await api.getCategories();
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  return (
    <header className="sticky top-0 z-50 glass border-b border-border/50">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <img 
              src="/logo.png" 
              alt="Chocados GDL Logo" 
              className="h-10 md:h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            <Link 
              to="/" 
              className="text-sm font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
              data-testid="nav-inicio"
            >
              Inicio
            </Link>
            
            {/* Inventario Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors outline-none">
                Inventario
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-card border-border w-48">
                <DropdownMenuItem asChild>
                  <Link to="/inventario" className="w-full cursor-pointer">
                    Todo
                  </Link>
                </DropdownMenuItem>
                {categories.map((cat) => (
                  <DropdownMenuItem key={cat.id} asChild>
                    <Link to={`/inventario/${cat.slug}`} className="w-full cursor-pointer">
                      {cat.nombre}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link 
              to="/servicios" 
              className="text-sm font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
              data-testid="nav-servicios"
            >
              Servicios
            </Link>
            
            <Link 
              to="/contacto" 
              className="text-sm font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
              data-testid="nav-contacto"
            >
              Contacto
            </Link>

          </nav>

          {/* Desktop Auth - hidden */}
          <div className="hidden lg:block" />

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle">
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-card border-border overflow-y-auto">
              <nav className="flex flex-col gap-2 mt-8">
                <Link 
                  to="/" 
                  onClick={() => setIsOpen(false)}
                  className="text-lg font-semibold uppercase tracking-wider text-foreground hover:text-primary transition-colors py-3 border-b border-border"
                >
                  Inicio
                </Link>
                
                {/* Mobile Inventario Section */}
                <div className="py-3 border-b border-border">
                  <p className="text-lg font-semibold uppercase tracking-wider text-foreground mb-2">
                    Inventario
                  </p>
                  <div className="pl-4 space-y-2">
                    <Link 
                      to="/inventario" 
                      onClick={() => setIsOpen(false)}
                      className="block text-muted-foreground hover:text-primary transition-colors py-1"
                    >
                      Todo
                    </Link>
                    {categories.map((cat) => (
                      <Link 
                        key={cat.id}
                        to={`/inventario/${cat.slug}`} 
                        onClick={() => setIsOpen(false)}
                        className="block text-muted-foreground hover:text-primary transition-colors py-1"
                      >
                        {cat.nombre}
                      </Link>
                    ))}
                  </div>
                </div>

                <Link 
                  to="/servicios" 
                  onClick={() => setIsOpen(false)}
                  className="text-lg font-semibold uppercase tracking-wider text-foreground hover:text-primary transition-colors py-3 border-b border-border"
                >
                  Servicios
                </Link>
                
                <Link 
                  to="/contacto" 
                  onClick={() => setIsOpen(false)}
                  className="text-lg font-semibold uppercase tracking-wider text-foreground hover:text-primary transition-colors py-3 border-b border-border"
                >
                  Contacto
                </Link>
                
                <div className="border-t border-border my-4" />
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
